import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { APIRequestStatus } from "common-ts/dist/models/APIRequestStatus";
import { mergeBulkResult } from "src/content/applications/Accounting/BankImport/mergeBulkResult";

// Accounting operations are temporarily routed through the livestream endpoint with an
// { accountingAction, payload } body (until dedicated endpoints are launched).
const ACCOUNTING_ENDPOINT =
    "https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/livestream-3820238";

// Posts an accounting action and returns the handler's response body, tolerating the
// validation 400s (Lambda-proxy turns statusCode 400 into an HTTP error that axios rejects).
// Exported for one-off calls that shouldn't touch this slice's state (e.g. the journal form's
// balance preview uses REPORT_GET without clobbering the Laporan tab's reportRows).
export async function callAccounting(accountingAction: string, payload: any = {}) {
    try {
        const response = await axios({
            method: "post",
            url: ACCOUNTING_ENDPOINT,
            data: JSON.stringify({ accountingAction, payload }),
        });
        return response.data;
    } catch (error: any) {
        if (error?.response?.data) {
            return error.response.data;
        }
        throw error;
    }
}

const initialState: any = {
    accountList: [],
    accountStatus: APIRequestStatus.Idle,

    entryList: [],
    entryStatus: APIRequestStatus.Idle,

    postStatus: APIRequestStatus.Idle,
    lastPostResult: null, // { success, errors? }

    reportRows: [],
    reportMeta: null, // { startDate, endDate }
    reportStatus: APIRequestStatus.Idle,

    // entries maps entryId -> the full journal entry, so ledger rows can show counter-accounts.
    ledger: null, // { accountCode, openingDebit, openingCredit, lines: [...], entries: {...}, entriesTruncated }
    ledgerStatus: APIRequestStatus.Idle,

    // [{ normalizedName, sampleName, accountCode, lastUsedAt }] — bank import pre-fill.
    counterpartyMap: [],
    counterpartyMapStatus: APIRequestStatus.Idle,

    bulkPostStatus: APIRequestStatus.Idle,
    bulkPostResult: null, // { success, results: [...], summary: {posted, duplicate, failed} } | { success:false, errors }
};

export const fetchAccounts = createAsyncThunk("accounting/fetchAccounts", async () => {
    const data = await callAccounting("ACCOUNTS_GET");
    return data;
});

export const postAccount = createAsyncThunk("accounting/postAccount", async (arg: any, thunkAPI) => {
    const data = await callAccounting("ACCOUNT_POST", arg);
    if (data?.success) {
        thunkAPI.dispatch(fetchAccounts());
    }
    return data;
});

export const fetchJournalEntries = createAsyncThunk(
    "accounting/fetchJournalEntries",
    async (arg: { startDate?: string; endDate?: string } | undefined) => {
        const data = await callAccounting("JOURNAL_ENTRIES_GET", arg ?? {});
        return data;
    }
);

export const postJournalEntry = createAsyncThunk("accounting/postJournalEntry", async (arg: any, thunkAPI) => {
    const data = await callAccounting("JOURNAL_ENTRY_POST", arg);
    if (data?.success) {
        thunkAPI.dispatch(fetchJournalEntries({ startDate: arg.date, endDate: arg.date }));
    }
    return data;
});

export const fetchReport = createAsyncThunk(
    "accounting/fetchReport",
    async (arg: { startDate?: string; endDate?: string; branchId?: string; businessLineId?: string } | undefined) => {
        const data = await callAccounting("REPORT_GET", arg ?? {});
        return data;
    }
);

export const fetchAccountLedger = createAsyncThunk(
    "accounting/fetchAccountLedger",
    async (arg: { accountCode: string; startDate?: string; endDate?: string; branchId?: string }) => {
        const data = await callAccounting("ACCOUNT_LEDGER_GET", arg);
        return data;
    }
);

// Learned source/destination-name -> ledger-account mappings (bank import pre-fill).
export const fetchCounterpartyMap = createAsyncThunk("accounting/fetchCounterpartyMap", async () => {
    const data = await callAccounting("COUNTERPARTY_MAP_GET");
    return data;
});

// Bulk statement import. Each entry is individually idempotent on its bank-ref sourceRef,
// so re-submitting after a partial failure (or re-uploading an overlapping PDF) is safe.
export const postBulkJournalEntries = createAsyncThunk(
    "accounting/postBulkJournalEntries",
    async (arg: { bankAccountCode: string; entries: any[]; accumulate?: boolean }, thunkAPI) => {
        const { accumulate, ...payload } = arg;
        const data = await callAccounting("JOURNAL_ENTRIES_BULK_POST", payload);
        if (data?.summary) {
            thunkAPI.dispatch(fetchJournalEntries(undefined));
            thunkAPI.dispatch(fetchCounterpartyMap());
        }
        return data;
    }
);

export const closeBook = createAsyncThunk(
    "accounting/closeBook",
    async (arg: { closeDate: string }, thunkAPI) => {
        const data = await callAccounting("CLOSE_BOOK", arg);
        if (data?.success) {
            thunkAPI.dispatch(fetchAccounts());
        }
        return data;
    }
);

const slice = createSlice({
    name: "accounting",
    initialState,
    reducers: {
        clearLastPostResult(state) {
            state.lastPostResult = null;
        },
        // Bank import: starting a new review/file must not show a previous batch's result
        // (a stale success would also re-lock the fresh review's submit button).
        clearBulkPostResult(state) {
            state.bulkPostResult = null;
            state.bulkPostStatus = APIRequestStatus.Idle;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchAccounts.pending, (state) => {
                state.accountStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchAccounts.fulfilled, (state, action) => {
                state.accountStatus = APIRequestStatus.Success;
                state.accountList = action.payload?.accountList ?? [];
            })
            .addCase(fetchAccounts.rejected, (state) => {
                state.accountStatus = APIRequestStatus.Failure;
            })
            .addCase(fetchJournalEntries.pending, (state) => {
                state.entryStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchJournalEntries.fulfilled, (state, action) => {
                state.entryStatus = APIRequestStatus.Success;
                state.entryList = action.payload?.entryList ?? [];
            })
            .addCase(fetchJournalEntries.rejected, (state) => {
                state.entryStatus = APIRequestStatus.Failure;
            })
            .addCase(postJournalEntry.pending, (state) => {
                state.postStatus = APIRequestStatus.RequestInProgress;
                state.lastPostResult = null;
            })
            .addCase(postJournalEntry.fulfilled, (state, action) => {
                state.postStatus = APIRequestStatus.Success;
                state.lastPostResult = action.payload ?? null;
            })
            .addCase(postJournalEntry.rejected, (state) => {
                state.postStatus = APIRequestStatus.Failure;
                state.lastPostResult = { success: false, errors: ["Request failed"] };
            })
            .addCase(postAccount.fulfilled, (state, action) => {
                state.lastPostResult = action.payload ?? null;
            })
            .addCase(fetchReport.pending, (state) => {
                state.reportStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchReport.fulfilled, (state, action) => {
                state.reportStatus = APIRequestStatus.Success;
                state.reportRows = action.payload?.reportRows ?? [];
                state.reportMeta = {
                    startDate: action.payload?.startDate,
                    endDate: action.payload?.endDate,
                };
            })
            .addCase(fetchReport.rejected, (state) => {
                state.reportStatus = APIRequestStatus.Failure;
            })
            .addCase(fetchAccountLedger.pending, (state) => {
                state.ledgerStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchAccountLedger.fulfilled, (state, action) => {
                state.ledgerStatus = APIRequestStatus.Success;
                state.ledger = action.payload ?? null;
            })
            .addCase(fetchAccountLedger.rejected, (state) => {
                state.ledgerStatus = APIRequestStatus.Failure;
            })
            .addCase(fetchCounterpartyMap.pending, (state) => {
                state.counterpartyMapStatus = APIRequestStatus.RequestInProgress;
            })
            .addCase(fetchCounterpartyMap.fulfilled, (state, action) => {
                state.counterpartyMapStatus = APIRequestStatus.Success;
                state.counterpartyMap = action.payload?.mappings ?? [];
            })
            .addCase(fetchCounterpartyMap.rejected, (state) => {
                state.counterpartyMapStatus = APIRequestStatus.Failure;
            })
            .addCase(postBulkJournalEntries.pending, (state) => {
                state.bulkPostStatus = APIRequestStatus.RequestInProgress;
                state.bulkPostResult = null;
            })
            .addCase(postBulkJournalEntries.fulfilled, (state, action) => {
                state.bulkPostStatus = APIRequestStatus.Success;
                const incoming = action.payload ?? null;
                // A statement larger than the backend's per-batch cap is posted in chunks;
                // every chunk after the first merges into the running result so the review
                // screen reports the whole import rather than just the final slice.
                state.bulkPostResult = mergeBulkResult(
                    state.bulkPostResult,
                    incoming,
                    Boolean(action.meta.arg?.accumulate)
                );
            })
            .addCase(postBulkJournalEntries.rejected, (state) => {
                state.bulkPostStatus = APIRequestStatus.Failure;
                state.bulkPostResult = { success: false, errors: ["Request gagal"] };
            });
    },
});

export const { clearLastPostResult, clearBulkPostResult } = slice.actions;

export default slice.reducer;
